// S9 Install (REQ-1.2, REQ-10.7): the snippet, the app key, the SDK steps (a copy the team keeps, DEP-8 B), the
// Content Security Policy entries and the test-capture links, from GET /api/apps/:appId/install. Rotating the key
// replaces the old one at once. No extension is distributed (DEP-8 B), so M4 serves `extensionNote` as null and
// the extension steps show only when it isn't (a day-2 extension release brings them back with no page change).
import type { AppView } from '@feedback/shared/contracts/apps';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { errorText } from '../../lib/admin-api';
import { Button, CodeBlock, ConfirmModal, CopyButton, Loading, Notice, Panel } from '../../ui/primitives';
import { appPath, installQuery, useAppWrite } from '../app-data';

export function InstallSection({ app }: { app: AppView }) {
  const install = useQuery(installQuery(app.id));
  const [rotating, setRotating] = useState(false);
  const rotate = useAppWrite(app.id, () => api.post<AppView>(appPath(app.id, '/keys/rotate')), 'App key rotated');

  if (!install.data) {
    return install.isError ? (
      <Notice tone="danger" title="The install details couldn’t be shown">
        {errorText(install.error)}
      </Notice>
    ) : (
      <Loading label="Loading the install details" />
    );
  }
  const info = install.data;
  const csp = [
    `script-src ${info.csp.scriptSrc}`,
    `connect-src ${info.csp.connectSrc}`,
    `img-src ${info.csp.imgSrc}`,
  ].join('\n');

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Install the widget" description="Add this snippet to every page of the app, just before </body>.">
        <CodeBlock label="Snippet" code={info.snippet} />
      </Panel>

      <Panel
        title="App key"
        description="Public: it goes in the snippet. Rotate it if it was used somewhere it shouldn’t be."
      >
        <div className="flex flex-wrap items-center gap-2">
          <code className="rounded bg-surface-raised px-2 py-1 font-mono text-sm break-all">{info.appKey}</code>
          <CopyButton text={info.appKey} label="Copy app key" />
          {app.can.manageSettings ? (
            <Button variant="secondary" onClick={() => setRotating(true)}>
              Rotate key
            </Button>
          ) : null}
        </div>
        {app.key ? <p className="mt-1 text-xs text-muted">Created {formatDateTime(app.key.createdAt)}</p> : null}
      </Panel>

      <Panel
        title="SDK"
        description={`For single-page apps that sign people in. The SDK (${info.sdk.packageName}) isn’t published to a package registry: an app that needs it gets a copy to keep in its own repository.`}
      >
        <CodeBlock label="SDK usage" code={info.sdk.usage} />
      </Panel>

      {info.extensionNote !== null ? (
        <Panel title="Browser extension">
          <p className="text-sm whitespace-pre-line">{info.extensionNote}</p>
        </Panel>
      ) : null}

      <Panel
        title="Content Security Policy"
        description="If the app sends a Content Security Policy, add the tool’s origin to these directives."
      >
        <CodeBlock label="CSP entries" code={csp} />
      </Panel>

      <Panel
        title="Test captures"
        description="Open an address in test-capture mode to see what a report’s screenshot would show."
      >
        <ul className="flex flex-col gap-1">
          {info.testCaptureLinks.length === 0 ? <li className="text-sm text-muted">No addresses yet.</li> : null}
          {info.testCaptureLinks.map((link) => (
            <li key={link.url}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm underline"
              >
                {link.environment}: <span className="font-mono text-xs break-all">{link.url}</span>
                <ExternalLink aria-hidden="true" className="size-3.5" />
              </a>
            </li>
          ))}
        </ul>
      </Panel>

      <ConfirmModal
        open={rotating}
        onOpenChange={(open) => {
          setRotating(open);
          if (!open) rotate.reset();
        }}
        title="Rotate the app key?"
        description="The old key stops working at once: update the snippet on every page of the app."
        confirmLabel="Rotate key"
        danger
        busy={rotate.isPending}
        error={rotate.isError ? errorText(rotate.error) : undefined}
        onConfirm={() => rotate.mutate(undefined, { onSuccess: () => setRotating(false) })}
      />
    </div>
  );
}
